/**
 * Cut a room exactly along a chain of walls.
 *
 * A room's outline runs on wall FACES, so the cut is the chain's two face
 * polylines (`chain.js: facePolylines`), each extended or clipped at both ends
 * to where it meets the room's boundary. The LEFT piece is the boundary walked
 * between the left face's two hits plus the left face itself; the RIGHT piece
 * the same for the right face. The strip between them is the wall body, which
 * belongs to neither room.
 *
 * Every piece vertex is therefore an original boundary vertex (the same
 * object), a mitre point of a face polyline, or one of the four boundary hits.
 * No polygon clipping, no growing, no rounding — `roomRegions` re-rounds every
 * coordinate and sits 4 mm inside the faces, so a wall carried verbatim would
 * sit off the new outline. That exactness is what the conservation check in
 * the tests relies on: left + right + strip = the room.
 *
 * Pure. X-Z plane.
 */

const { signedAreaXZ } = require('../geometry/polygonSplit');
const {
  isPointInsidePolygon,
  isPointInsideOrOnPolygon,
} = require('../geometry/pointInPolygon');
const { objectFootprint } = require('../geometry/objectFootprint');
const { distanceToPolygonBoundary, distanceToSegment } = require('./geometry');
const { TOUCH_TOL_M, facePolylines, stretchFrame } = require('./chain');
const { IN_PIECE_TOL_M } = require('./pointPiece');

/** A piece thinner than this (2·area ÷ perimeter) is a mis-tap, not a room. */
const MIN_PIECE_WIDTH_M = 0.05;
/** An edge this close to a face polyline, along its length, IS the cut. */
const CUT_EDGE_TOL_M = 0.02;
/** A boundary hit this close to a vertex is that vertex. */
const VERTEX_SNAP_M = 1e-9;
/** Parameter slack on a ring edge when intersecting. */
const EDGE_EPS = 1e-12;
/** The along-wall slack a face hit may have beyond its geometric position. */
const FACE_HIT_SLACK_M = 1e-6;

const dist = (a, b) => Math.hypot(a.x - b.x, a.z - b.z);
const no = (reason, detail) => ({ ok: false, reason, ...(detail || {}) });

/** 2 × |area| ÷ perimeter — the width of a strip of this area and outline. */
function pieceWidth(polygon) {
  const ring = polygon || [];
  let per = 0;
  for (let i = 0; i < ring.length; i++) {
    per += dist(ring[i], ring[(i + 1) % ring.length]);
  }
  return per > 0 ? (2 * Math.abs(signedAreaXZ(ring))) / per : 0;
}

/** Cross product of 2-vectors in the X-Z plane. */
const cross = (a, b) => a.x * b.z - a.z * b.x;

/**
 * First crossing of the ray `o + t·d` (t > 0) with the ring.
 * @returns {{ edge, u, t, point }|null} `point` lies exactly on the edge.
 */
function rayHit(o, d, ring) {
  let best = null;
  for (let i = 0; i < ring.length; i++) {
    const r0 = ring[i];
    const r1 = ring[(i + 1) % ring.length];
    const e = { x: r1.x - r0.x, z: r1.z - r0.z };
    const den = cross(d, e);
    if (Math.abs(den) < 1e-15) continue; // parallel
    const w = { x: r0.x - o.x, z: r0.z - o.z };
    const t = cross(w, e) / den;
    const u = cross(w, d) / den;
    if (t <= 1e-12 || u < -EDGE_EPS || u > 1 + EDGE_EPS) continue;
    if (!best || t < best.t) {
      const uc = Math.min(1, Math.max(0, u));
      best = {
        edge: i,
        u: uc,
        t,
        point: { x: r0.x + e.x * uc, z: r0.z + e.z * uc },
        edgeDir: e,
      };
    }
  }
  return best;
}

/**
 * Every intersection of segment p→q with the ring's edges, as
 * `{ s, point, edgeDir }` (s along p→q). Collinear overlaps report both ends.
 */
function segmentCrossings(p, q, ring) {
  const d = { x: q.x - p.x, z: q.z - p.z };
  const out = [];
  for (let i = 0; i < ring.length; i++) {
    const r0 = ring[i];
    const r1 = ring[(i + 1) % ring.length];
    const e = { x: r1.x - r0.x, z: r1.z - r0.z };
    const den = cross(d, e);
    const w = { x: r0.x - p.x, z: r0.z - p.z };
    if (Math.abs(den) < 1e-15) {
      // Parallel: a crossing only when collinear and overlapping.
      if (Math.abs(cross(w, d)) > 1e-12 * (Math.hypot(d.x, d.z) || 1)) continue;
      const len2 = d.x * d.x + d.z * d.z || 1;
      const s0 = (w.x * d.x + w.z * d.z) / len2;
      const s1 = ((r1.x - p.x) * d.x + (r1.z - p.z) * d.z) / len2;
      const lo = Math.max(0, Math.min(s0, s1));
      const hi = Math.min(1, Math.max(s0, s1));
      if (hi < lo) continue;
      for (const s of [lo, hi]) {
        out.push({
          s,
          point: { x: p.x + d.x * s, z: p.z + d.z * s },
          edgeDir: e,
        });
      }
      continue;
    }
    const s = cross(w, e) / den;
    const u = cross(w, d) / den;
    if (s < -EDGE_EPS || s > 1 + EDGE_EPS) continue;
    if (u < -EDGE_EPS || u > 1 + EDGE_EPS) continue;
    out.push({ s, point: { x: p.x + d.x * s, z: p.z + d.z * s }, edgeDir: e });
  }
  return out;
}

/** True when the segment from boundary point `from` to `to` crosses back into
 *  the ring anywhere past `from`. */
function reentersRoom(from, to, ring) {
  const len = dist(from, to);
  if (!(len > 0)) return false;
  return segmentCrossings(from, to, ring).some(
    (c) => c.s * len > FACE_HIT_SLACK_M
  );
}

/** |sin| of the angle between two directions. */
function sinBetween(a, b) {
  const la = Math.hypot(a.x, a.z) || 1;
  const lb = Math.hypot(b.x, b.z) || 1;
  return Math.abs(cross(a, b)) / (la * lb);
}

/**
 * The centreline: both ends on the boundary, everything between strictly
 * inside. Returns a refusal or null.
 */
function checkCentreline(points, ring) {
  const first = points[0];
  const last = points[points.length - 1];
  const off = [];
  for (const [which, p] of [
    ['start', first],
    ['end', last],
  ]) {
    const d = distanceToPolygonBoundary(p, ring);
    if (d <= TOUCH_TOL_M) continue;
    // Outside the room and not touching it: the chain leaves the room.
    if (!isPointInsidePolygon(p, ring)) return no('crosses-boundary');
    off.push({ which, distance: d });
  }
  if (off.length) return no('endpoint-off-boundary', { ends: off });

  for (let i = 1; i + 1 < points.length; i++) {
    const p = points[i];
    // On the edge, not out of the room: said apart, since "leaves the room"
    // would send the user looking for a wall that does not leave it. Asked
    // first, as a point on the edge may read as outside.
    if (distanceToPolygonBoundary(p, ring) <= TOUCH_TOL_M) {
      return no('bend-on-boundary');
    }
    if (!isPointInsidePolygon(p, ring)) return no('crosses-boundary');
  }
  const lastSeg = points.length - 2;
  for (let i = 0; i + 1 < points.length; i++) {
    const p = points[i];
    const q = points[i + 1];
    const mid = { x: (p.x + q.x) / 2, z: (p.z + q.z) / 2 };
    if (!isPointInsidePolygon(mid, ring)) return no('crosses-boundary');
    const segDir = { x: q.x - p.x, z: q.z - p.z };
    for (const c of segmentCrossings(p, q, ring)) {
      // A chain end within TOUCH_TOL of the boundary meets it a little before
      // or after the end itself — more so the shallower the approach.
      const slack = TOUCH_TOL_M / Math.max(sinBetween(segDir, c.edgeDir), 0.1);
      if (i === 0 && dist(c.point, first) <= slack) continue;
      if (i === lastSeg && dist(c.point, last) <= slack) continue;
      return no('crosses-boundary');
    }
  }
  return null;
}

/**
 * One face polyline trimmed to the room: extended or clipped at each end to
 * the first boundary crossing along its end segment's line.
 *
 * `sign` is +1 for the left face, -1 for the right. The face hit at each end
 * may fall short of the centreline end (the boundary is nearer on that side),
 * and may run past it by up to `half × cot θ` — where the face of a wall
 * meeting the boundary there obliquely meets it. Further than that, the face
 * line is running on past the wall's END: the end stands at an inside corner
 * (or a few centimetres beside one, or on the end of a stub thinner than the
 * wall), and the boundary edge it meets carries on beside the face instead of
 * crossing it. The wall stops at its end, so the face does too, and the piece
 * closes round the end: from the face's end point along the wall's end, toward
 * the centreline, to the boundary (`closeRoundEnd`). What is left of the room
 * is still exactly the room minus the wall's body.
 *
 * @returns {{ ok: true, trimmed, startHit, endHit, startCap, endCap }
 *           | refusal} `trimmed` runs from the start face point to the end
 *   one (a hit, or the wall's end when that end is capped); `startHit` /
 *   `endHit` are where the outline is met (the cap's hit when capped);
 *   `startCap` / `endCap` are `{ a, out }` — the capped end's face point and
 *   the direction away from the chain there — or null.
 */
function trimFace(face, chain, sign, ring) {
  const st = chain.stretches;
  const m = st.length;
  const interior = face.slice(1, m); // mitre points
  for (const p of interior) {
    if (!isPointInsidePolygon(p, ring)) return no('sliver-side', { width: 0 });
  }
  const firstDir = stretchFrame(st[0]).dir;
  const lastDir = stretchFrame(st[m - 1]).dir;
  const startAnchor =
    m === 1
      ? { x: (face[0].x + face[1].x) / 2, z: (face[0].z + face[1].z) / 2 }
      : interior[0];
  const endAnchor = m === 1 ? startAnchor : interior[interior.length - 1];
  if (!isPointInsidePolygon(startAnchor, ring)) {
    return no('sliver-side', { width: 0 }); // the wall body runs out of the room
  }
  const startHit = rayHit(
    startAnchor,
    { x: -firstDir.x, z: -firstDir.z },
    ring
  );
  const endHit = rayHit(endAnchor, lastDir, ring);
  if (!startHit || !endHit) return no('sliver-side', { width: 0 });

  // The face hit must belong to THIS end of the wall: the face may stop short
  // of the end (the boundary is nearer on that side — a nib the wall's end
  // pokes into), but must not run on PAST it, out from under the wall. `out`
  // points away from the chain at each end.
  const ends = [
    {
      hit: startHit,
      p: chain.points[0],
      s: st[0],
      dir: firstDir,
      out: { x: -firstDir.x, z: -firstDir.z },
    },
    {
      hit: endHit,
      p: chain.points[chain.points.length - 1],
      s: st[m - 1],
      dir: lastDir,
      out: lastDir,
    },
  ];
  const met = [];
  for (const { hit, p, s, dir, out } of ends) {
    const { n } = stretchFrame(s);
    const proj = {
      x: p.x + n.x * sign * s.half,
      z: p.z + n.z * sign * s.half,
    };
    const along =
      (hit.point.x - proj.x) * out.x + (hit.point.z - proj.z) * out.z;
    const sin = sinBetween(dir, hit.edgeDir);
    const cos = Math.sqrt(Math.max(0, 1 - sin * sin));
    const allowed =
      (s.half * cos + TOUCH_TOL_M) / Math.max(sin, 1e-6) + FACE_HIT_SLACK_M;
    if (along > allowed) {
      // Past the wall's end: close round it.
      const capHit = closeRoundEnd(
        proj,
        { x: -n.x * sign, z: -n.z * sign },
        s,
        ring
      );
      if (!capHit) return no('crosses-boundary');
      met.push({ point: proj, hit: capHit, cap: { a: proj, out } });
      continue;
    }
    // Short of the end: between the hit and the wall's end the face line must
    // stay out of the room. If it comes back in, the wall overlaps the end of
    // another (a scanned stub carved into the outline) and closes off the
    // room beyond it — a third piece.
    if (along < -FACE_HIT_SLACK_M && reentersRoom(hit.point, proj, ring)) {
      return no('multiple-pieces');
    }
    met.push({ point: hit.point, hit, cap: null });
  }
  const [S, E] = met;

  const trimmed = [S.point, ...interior, E.point];
  // Middle segments are not covered by the ray construction: they must not
  // meet the boundary at all.
  for (let i = 1; i + 2 < trimmed.length; i++) {
    if (segmentCrossings(trimmed[i], trimmed[i + 1], ring).length) {
      return no('sliver-side', { width: 0 });
    }
  }
  return {
    ok: true,
    trimmed,
    startHit: S.hit,
    endHit: E.hit,
    startCap: S.cap,
    endCap: E.cap,
  };
}

/**
 * Where a wall's END, followed from the face's end point `a` toward the
 * centreline (`toward`), meets the room's outline — at the latest a touch past
 * the centreline's own end, which is on the outline (within `TOUCH_TOL_M`).
 * Null when it does not: the end is not on the outline after all.
 */
function closeRoundEnd(a, toward, stretch, ring) {
  const hit = rayHit(a, toward, ring);
  if (!hit) return null;
  const slack = TOUCH_TOL_M / Math.max(sinBetween(toward, hit.edgeDir), 0.1);
  if (hit.t > stretch.half + slack + FACE_HIT_SLACK_M) return null;
  return hit;
}

/** A hit snapped onto a ring vertex when it lands on one. */
function placeHit(hit, ring) {
  const n = ring.length;
  const a = ring[hit.edge];
  const b = ring[(hit.edge + 1) % n];
  if (dist(hit.point, a) <= VERTEX_SNAP_M) {
    return { pos: hit.edge, point: a };
  }
  if (dist(hit.point, b) <= VERTEX_SNAP_M) {
    return { pos: (hit.edge + 1) % n, point: b };
  }
  return { pos: hit.edge + hit.u, point: hit.point };
}

/** Ring vertices strictly between positions `from` and `to`, walking forward. */
function verticesBetween(ring, from, to) {
  const n = ring.length;
  const span = (((to - from) % n) + n) % n;
  const out = [];
  for (let k = 0; k < n; k++) {
    const off = (((k - from) % n) + n) % n;
    if (off > 1e-12 && off < span - 1e-12) out.push({ off, v: ring[k] });
  }
  out.sort((x, y) => x.off - y.off);
  return out.map((o) => o.v);
}

/** Drop consecutive duplicates (and a closing duplicate). */
function dedupe(poly) {
  const out = [];
  for (const p of poly) {
    if (out.length && dist(out[out.length - 1], p) <= VERTEX_SNAP_M) continue;
    out.push(p);
  }
  while (out.length > 1 && dist(out[0], out[out.length - 1]) <= VERTEX_SNAP_M) {
    out.pop();
  }
  return out;
}

/**
 * The piece on the `sign` side of one trimmed face: the boundary arc that,
 * closed with the face, contains a point just off the face on that side.
 */
function pieceForFace(trimmed, startPos, endPos, ring, sign) {
  const s = trimmed[0];
  const e = trimmed[trimmed.length - 1];
  const inner = trimmed.slice(1, -1);
  const candA = dedupe([
    s,
    ...inner,
    e,
    ...verticesBetween(ring, endPos.pos, startPos.pos),
  ]);
  const candB = dedupe([
    e,
    ...inner.slice().reverse(),
    s,
    ...verticesBetween(ring, startPos.pos, endPos.pos),
  ]);
  // Probe just off the face's longest segment, on this face's side.
  let best = 0;
  let bestLen = -1;
  for (let i = 0; i + 1 < trimmed.length; i++) {
    const len = dist(trimmed[i], trimmed[i + 1]);
    if (len > bestLen) {
      bestLen = len;
      best = i;
    }
  }
  const p = trimmed[best];
  const q = trimmed[best + 1];
  const len = bestLen || 1;
  const nrm = { x: -(q.z - p.z) / len, z: (q.x - p.x) / len };
  const eps = Math.min(1e-6, len / 4);
  const probe = {
    x: (p.x + q.x) / 2 + nrm.x * sign * eps,
    z: (p.z + q.z) / 2 + nrm.z * sign * eps,
  };
  if (candA.length >= 3 && isPointInsidePolygon(probe, candA)) return candA;
  if (candB.length >= 3 && isPointInsidePolygon(probe, candB)) return candB;
  return null;
}

/**
 * The outline between one chain end's two face hits: of the two ways round
 * the ring between them, the one whose vertices are in neither piece (the
 * short way, in practice). An open polyline from one hit to the other.
 */
function gapBetween(a, b, ring, pieceVertices) {
  const forward = [a.point, ...verticesBetween(ring, a.pos, b.pos), b.point];
  const backward = [b.point, ...verticesBetween(ring, b.pos, a.pos), a.point];
  const inPiece = (poly) => poly.slice(1, -1).some((v) => pieceVertices.has(v));
  const len = (poly) =>
    poly.reduce((s, p, i) => (i ? s + dist(poly[i - 1], p) : 0), 0);
  // The gap's own vertices belong to no piece; a way round through a piece's
  // corners is that piece's outline.
  if (inPiece(forward) !== inPiece(backward)) {
    return inPiece(forward) ? backward : forward;
  }
  return len(forward) <= len(backward) ? forward : backward;
}

/** `polygon` wound like `reference`. */
function matchWinding(polygon, reference) {
  const want = signedAreaXZ(reference) >= 0;
  const got = signedAreaXZ(polygon) >= 0;
  return want === got ? polygon : polygon.slice().reverse();
}

/** Shortest distance from `p` to an open polyline. */
function distanceToPolyline(p, line) {
  let best = Infinity;
  for (let i = 0; i + 1 < line.length; i++) {
    best = Math.min(best, distanceToSegment(p, line[i], line[i + 1]));
  }
  return best;
}

/**
 * Cut `polygon` along `chain`.
 *
 * @param {{x,z}[]} polygon the room's floor ring (any winding)
 * @param {object} chain a `buildChain` result
 * @returns {{ ok: true, left: { polygon, area }, right: { polygon, area },
 *             sideOf(p) → 1|-1|0, inWall(p) → boolean, straddles(obj) → boolean,
 *             isCutEdge(a, b) → boolean, hits: {x,z}[],
 *             faces: { left: {x,z}[], right: {x,z}[] },
 *             caps: { left: {a,b,out,wallId}[], right: {a,b,out,wallId}[] } }
 *   | { ok: false, reason: 'endpoint-off-boundary', ends }
 *   | { ok: false, reason: 'crosses-boundary' }
 *   | { ok: false, reason: 'bend-on-boundary' } — a bend touches the outline
 *   | { ok: false, reason: 'multiple-pieces' } — the wall overlaps the end of
 *       another and would close off a third piece of the room
 *   | { ok: false, reason: 'sliver-side', width }}
 *   Pieces come back in `polygon`'s winding. `sideOf` is 1 inside-or-on the
 *   left piece (1 cm), -1 the right, 0 otherwise (the wall body, or outside).
 */
function cutRoomAlongChain(polygon, chain) {
  const ring = polygon || [];
  if (ring.length < 3 || !chain?.ok || !chain.stretches?.length) {
    return no('crosses-boundary');
  }
  const bad = checkCentreline(chain.points, ring);
  if (bad) return bad;

  const faces = facePolylines(chain);
  const L = trimFace(faces.left, chain, 1, ring);
  if (!L.ok) return L;
  const R = trimFace(faces.right, chain, -1, ring);
  if (!R.ok) return R;

  const place = (hit) => placeHit(hit, ring);
  const Ls = place(L.startHit);
  const Le = place(L.endHit);
  const Rs = place(R.startHit);
  const Re = place(R.endHit);
  // Each face, one point per joint of the chain — its two ends a hit (snapped
  // onto a vertex it lands on, so it IS that vertex) or, at a capped end, the
  // wall's end — and the piece's whole cut edge: the face plus each cap.
  const faceOf = (T, s, e) => [
    T.startCap ? T.trimmed[0] : s.point,
    ...T.trimmed.slice(1, -1),
    T.endCap ? T.trimmed[T.trimmed.length - 1] : e.point,
  ];
  const outlineOf = (T, s, e) => [
    s.point,
    ...faceOf(T, s, e).slice(T.startCap ? 0 : 1, T.endCap ? undefined : -1),
    e.point,
  ];
  const st = chain.stretches;
  const capsOf = (T, s, e) =>
    [
      T.startCap && { ...T.startCap, b: s.point, wallId: st[0].wallId },
      T.endCap && { ...T.endCap, b: e.point, wallId: st[st.length - 1].wallId },
    ].filter(Boolean);
  const Lt = faceOf(L, Ls, Le);
  const Rt = faceOf(R, Rs, Re);
  const Lo = outlineOf(L, Ls, Le);
  const Ro = outlineOf(R, Rs, Re);

  const leftPoly = pieceForFace(Lo, Ls, Le, ring, 1);
  const rightPoly = pieceForFace(Ro, Rs, Re, ring, -1);
  if (!leftPoly || !rightPoly) return no('crosses-boundary');

  const left = matchWinding(leftPoly, ring);
  const right = matchWinding(rightPoly, ring);
  const leftArea = Math.abs(signedAreaXZ(left));
  const rightArea = Math.abs(signedAreaXZ(right));
  // The two faces must not cross: the pieces cannot cover more than the room.
  if (leftArea + rightArea > Math.abs(signedAreaXZ(ring)) + 1e-9) {
    return no('crosses-boundary');
  }
  const width = Math.min(pieceWidth(left), pieceWidth(right));
  if (width < MIN_PIECE_WIDTH_M) return no('sliver-side', { width });

  const sideOf = (p) => {
    if (!p) return 0;
    if (isPointInsideOrOnPolygon(p, left, IN_PIECE_TOL_M)) return 1;
    if (isPointInsideOrOnPolygon(p, right, IN_PIECE_TOL_M)) return -1;
    return 0;
  };
  const straddles = (obj) => {
    const corners = objectFootprint(obj);
    if (!corners) return false;
    const sides = new Set(corners.map(sideOf));
    return sides.has(1) && sides.has(-1);
  };
  const along = (line) => (a, b) => {
    const mid = { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 };
    return [a, mid, b].every(
      (p) => distanceToPolyline(p, line) <= CUT_EDGE_TOL_M
    );
  };
  const onLeft = along(Lo);
  const onRight = along(Ro);

  return {
    ok: true,
    left: { polygon: left, area: leftArea },
    right: { polygon: right, area: rightArea },
    sideOf,
    // In the wall's own body: inside the room, in neither piece.
    inWall: (p) => !!p && sideOf(p) === 0 && isPointInsidePolygon(p, ring),
    straddles,
    isCutEdge: (a, b) => onLeft(a, b) || onRight(a, b),
    hits: [Ls.point, Le.point, Rs.point, Re.point],
    // What each end of the chain stands on: the stretch of the room's outline
    // between its two face hits, which belongs to neither piece.
    gaps: [Ls, Le].map((a, i) =>
      gapBetween(a, [Rs, Re][i], ring, new Set([...left, ...right]))
    ),
    faces: { left: Lt, right: Rt },
    // A capped end's edge on each piece: the wall's end, from the face to the
    // outline (`{ a, b, out, wallId }`, `out` pointing away from the chain —
    // into the piece — and `wallId` the stretch's wall).
    caps: { left: capsOf(L, Ls, Le), right: capsOf(R, Rs, Re) },
  };
}

module.exports = {
  MIN_PIECE_WIDTH_M,
  IN_PIECE_TOL_M,
  CUT_EDGE_TOL_M,
  pieceWidth,
  cutRoomAlongChain,
};
